import { createApiInstance } from './handler';
import { getStoredFilter, setStoredFilter } from '../components/shared/filters/filter-common';
import { getClientConfiguration } from './client';

export const DEFAULT_METADATA_PATH = 'value';
export const USER_INFO_ATTRIBUTE = 'user-info';

let metadataAttributes = null;

export const buildMetadata = (metadata) => {
  if (metadata && metadata.length) {
    const transformedMetadata = {};
    metadata.forEach((item) => {
      const renderPaths = [];
      item.render_paths.forEach((path) => {
        renderPaths.push(path);
      });
      transformedMetadata[item.attribute.name] = {
        displayName: item.attribute.display_name,
        name: item.attribute.name,
        id: item.attribute.id,
        renderPaths,
        item: JSON.parse(item.value.json),
      };
    });
    return transformedMetadata;
  }

  return null;
};

export const getMetadataAttributes = async (auth) => {
  try {
    if (!metadataAttributes) {
      const retrievedAttributes = {};
      const response = await createApiInstance(auth, 'client').get('/metadata/attributes?limit=1000');
      if (response.data?.data?.length) {
        response.data.data.forEach((attribute) => {
          retrievedAttributes[attribute.id] = {
            id: attribute.id,
            name: attribute.name,
            displayName: attribute.display_name,
          };
        });
        metadataAttributes = retrievedAttributes;
      }
    }
    return metadataAttributes;
  } catch (error) {
    console.error(error);
  }
};

export const getUserInfoAttributeId = async (auth) => {
  return findMetadataAttributeIdByName(auth, USER_INFO_ATTRIBUTE);
};

export const findMetadataAttributeIdByName = async (auth, name) => {
  const metadataAttributes = await getMetadataAttributes(auth);
  if (metadataAttributes) {
    for (const [key, value] of Object.entries(metadataAttributes)) {
      if (value.name === name) {
        return key;
      }
    }
  }
};

export const findMetadataAttributeNameById = async (auth, id) => {
  const metadataAttributes = await getMetadataAttributes(auth);
  if (metadataAttributes && id) {
    return metadataAttributes[`${id}`]?.name;
  }
};

export const getMetadataValue = (metadata, attribute, pathSpec = DEFAULT_METADATA_PATH, defaultValue = undefined) => {
  if (metadata) {
    const metadataValue = metadata[attribute];
    if (metadataValue) {
      return metadataValue.item ? metadataValue.item[pathSpec] : metadataValue[pathSpec];
    }
  }

  return defaultValue;
};

export const getMetadataUniqueValues = async (auth, attributeIds, pathSpec = DEFAULT_METADATA_PATH) => {
  try {
    const metadataValues = [];
    const strAttributesIds = attributeIds.join(',');
    const response = await createApiInstance(auth, 'client').get(`/metadata/values?attribute_ids=${strAttributesIds}`);
    if (response.data?.length) {
      const metadataAttributes = await getMetadataAttributes(auth);
      for (const data of response.data) {
        const attributeId = data.id;
        metadataValues.push({
          attributeId,
          values: data.values
            .map((value) => {
              return value[pathSpec];
            })
            .sort(),
          name: metadataAttributes[attributeId].name,
          displayName: metadataAttributes[attributeId].displayName,
        });
      }
    }
    return metadataValues;
  } catch (error) {
    console.error(error);
  }
};

export const buildStoredFilters = async (auth, users) => {
  const clientConfiguration = getClientConfiguration();
  let filters;
  if (auth.isDevelop()) {
    filters = clientConfiguration.develop?.dashboard?.filters?.metadata;
  } else {
    filters = clientConfiguration.select?.dashboard?.filters?.metadata;
  }
  if (filters) {
    let parentKey = undefined;
    const metadataAttributes = await getMetadataAttributes(auth);
    filters.forEach((attributeId) => {
      const attribute = metadataAttributes[attributeId];
      if (filters.includes(attribute.id)) {
        const parentFilter = parentKey ? getStoredFilter(parentKey) : undefined;
        const parentRelationships = parentKey ? {} : undefined;
        const distinctValues = [];
        const items = [];

        const attributeName = attribute.name;
        users.forEach((user) => {
          if (user.metadata && user.metadata[attributeName]) {
            const metadata = user.metadata;

            const value = getMetadataValue(metadata, attributeName);
            const lcValue = value ? value.toLowerCase() : '';

            if (value && !distinctValues.includes(value)) {
              distinctValues.push(value);
              items.push({
                value,
                lcValue,
                selected: true,
              });
            }

            if (parentFilter) {
              const parentValue = getMetadataValue(metadata, parentKey);
              if (parentValue) {
                let relationship = parentRelationships[parentValue.toLowerCase()];
                if (!relationship) {
                  relationship = [];
                  parentRelationships[parentValue.toLowerCase()] = relationship;
                }
                if (!relationship.includes(lcValue)) {
                  relationship.push(lcValue);
                }
              }
            }
          }
        });
        if (distinctValues.length > 1) {
          const displayPosition = filters.indexOf(attribute.id);
          setStoredFilter(
            attributeName,
            attribute.displayName,
            displayPosition,
            distinctValues,
            items,
            parentKey,
            parentRelationships
          );
          parentKey = attributeName;
        }
      }
    });
  }
};

export const getTargetFilters = async (auth) => {
  const userInfoAttributeId = await getUserInfoAttributeId(auth);
  const attributesFilter = [
    {
      attributeId: userInfoAttributeId,
      path: auth.isDevelop() ? 'develop' : 'select',
      value: true,
    },
    {
      attributeId: userInfoAttributeId,
      path: 'target',
      value: true,
    },
  ];
  const userMetadata = auth.metadata;
  if (userMetadata) {
    const userInfo = userMetadata[USER_INFO_ATTRIBUTE];
    if (userInfo.item?.filters?.target) {
      const targetFilter = userInfo.item.filters.target;
      const targetAttributeId = targetFilter.attributeId;
      let filterValue = targetFilter.value;
      if (!filterValue) {
        const attributeName = await findMetadataAttributeNameById(auth, targetAttributeId);
        const userAttribute = userMetadata[attributeName];
        if (userAttribute) {
          filterValue = userAttribute.item[targetFilter.path];
        }
      }
      if (Array.isArray(filterValue)) {
        filterValue.forEach((value) => {
          attributesFilter.push({
            attributeId: targetAttributeId,
            path: targetFilter.path,
            value: value,
          });
        });
      } else {
        attributesFilter.push({
          attributeId: targetAttributeId,
          path: targetFilter.path,
          value: filterValue,
        });
      }
    }
  }

  return attributesFilter;
};

export const buildMetadataQuery = (attributesFilter) => {
  let metadataQuery = '';
  for (let f = 0; f < attributesFilter.length; f++) {
    metadataQuery += `&metadata[${f}][id]=${attributesFilter[f].attributeId}&metadata[${f}][path]=${attributesFilter[f].path}&metadata[${f}][value]=${attributesFilter[f].value}`;
  }

  return metadataQuery;
};
